import { useState, useEffect, useRef, KeyboardEvent } from "react";

/**
 * A custom React hook that provides keyboard navigation functionality for selectable lists.
 *
 * This hook enables keyboard-friendly navigation through a list of items where users can navigate
 * using arrow keys, select items with Enter, and close/cancel with Escape.
 * It automatically handles scrolling the selected item into view and resets selection when the items change.
 *
 * @example
 * ```tsx
 * interface Service {
 *   id: string;
 *   name: string;
 *   uri: string;
 * }
 *
 * function ServiceList() {
 *   const [services, setServices] = useState<Service[]>([]);
 *   const [isEnabled, setIsEnabled] = useState(false);
 *
 *   const { selectedIndex, setSelectedIndex, itemRefs, handleKeyDown } =
 *     useSelectableListNavigation({
 *       items: services,
 *       isEnabled,
 *       onSelect: (service) => {
 *         window.location.href = service.uri;
 *       },
 *       onClose: () => {
 *         setIsEnabled(false);
 *       },
 *     });
 *
 *   return (
 *     <input
 *       onKeyDown={handleKeyDown}
 *     />
 *   );
 * }
 * ```
 *
 * @template T - The type of items in the list. Can be any type (e.g., Service, User, Product).
 *
 * @param options - Configuration options for the keyboard navigation hook.
 * @param options.items - Array of items to navigate through. The hook will reset the selected index when this array changes.
 * @param options.isEnabled - Boolean indicating whether keyboard navigation is currently enabled. Navigation only works when this is `true`.
 * @param options.onSelect - Callback function called when a user selects an item (via Enter key). Receives the selected item of type `T`.
 * @param options.onClose - Callback function called when Escape key is pressed. Should close the list and reset any related state.
 *
 * @returns An object containing:
 * @returns selectedIndex - The currently selected item index (-1 means no selection). Use this to highlight the selected item in your UI.
 * @returns setSelectedIndex - Function to programmatically set the selected index. Can be used for mouse hover interactions.
 * @returns itemRefs - React ref object containing refs to all dropdown items. Attach these refs to your dropdown items for automatic scrolling.
 * @returns handleKeyDown - Event handler function for keyboard events. Attach this to your input element's `onKeyDown` prop.
 *
 * @remarks
 * - Arrow Down: Moves selection down (wraps at bottom)
 * - Arrow Up: Moves selection up (stops at top, -1 means no selection)
 * - Enter: Selects the current item if one is selected, otherwise does nothing
 * - Escape: Closes the list via `onClose` callback
 * - The hook automatically scrolls the selected item into view when selection changes
 * - Selected index resets to -1 when the items array changes
 */
export function useSelectableListNavigation<T>({
  items,
  isEnabled,
  onSelect,
  onClose,
}: UseSelectableListNavigationOptions<T>): UseSelectableListNavigationReturn {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Reset selected index when items change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [items]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedIndex]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isEnabled || items.length === 0) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < items.length) {
          const selectedItem = items[selectedIndex];
          if (selectedItem) {
            onSelect(selectedItem);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        setSelectedIndex(-1);
        break;
    }
  };

  return {
    selectedIndex,
    setSelectedIndex,
    itemRefs,
    handleKeyDown,
  };
}

interface UseSelectableListNavigationOptions<T> {
  /** Array of items to navigate through. The hook will reset the selected index when this array changes. */
  items: T[];
  /** Boolean indicating whether keyboard navigation is currently enabled. Navigation only works when this is `true`. */
  isEnabled: boolean;
  /** Callback function called when a user selects an item (via Enter key). Receives the selected item of type `T`. */
  onSelect: (item: NoInfer<T>) => void;
  /** Callback function called when Escape key is pressed. Should close the list and reset any related state. */
  onClose: () => void;
}

interface UseSelectableListNavigationReturn {
  /** The currently selected item index (-1 means no selection). Use this to highlight the selected item in your UI. */
  selectedIndex: number;
  /** Function to programmatically set the selected index. Can be used for mouse hover interactions. */
  setSelectedIndex: (index: number | ((prev: number) => number)) => void;
  /** React ref object containing refs to all dropdown items. Attach these refs to your dropdown items for automatic scrolling. */
  itemRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  /** Event handler function for keyboard events. Attach this to your input element's `onKeyDown` prop. */
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}
